import { useEffect,useMemo,useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function AdminCategories(){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();
  const [categories,setCategories]=useState([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedCategory,setSelectedCategory]=useState(null);
  const [name,setName]=useState("");
  const [active,setActive]=useState(true);
  const [nameError,setNameError]=useState("");
  const [page,setPage]=useState(0);
  const [rowsPerPage,setRowsPerPage]=useState(10);

  const loadCategories=async()=>{
    try{
      setLoading(true);
      const response=await fetch("/competition/categories/",{credentials:"include"});
      const data=await readResponse(response);
      if(!response.ok) throw new Error(data.error||"No se pudieron cargar las categorías.");
      setCategories(Array.isArray(data)?data:[]);
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadCategories();
  },[]);

  const filteredCategories=useMemo(()=>{
    const value=search.trim().toLowerCase();
    if(!value) return categories;
    return categories.filter((category)=>String(category.name||"").toLowerCase().includes(value));
  },[categories,search]);

  useEffect(()=>{
    const maxPage=Math.max(0,Math.ceil(filteredCategories.length/rowsPerPage)-1);
    if(page>maxPage) setPage(maxPage);
  },[filteredCategories.length,rowsPerPage,page]);

  const rows=filteredCategories.slice(page*rowsPerPage,page*rowsPerPage+rowsPerPage);

  const handleCreate=()=>{
    setSelectedCategory(null);
    setName("");
    setActive(true);
    setNameError("");
    setModalOpen(true);
  };

  const handleEdit=(category)=>{
    setSelectedCategory(category);
    setName(category.name||"");
    setActive(category.active!==false);
    setNameError("");
    setModalOpen(true);
  };

  const handleClose=()=>{
    if(saving) return;
    setModalOpen(false);
    setSelectedCategory(null);
    setName("");
    setActive(true);
    setNameError("");
  };

  const handleSave=async()=>{
    const cleanName=name.trim();

    if(!cleanName){
      setNameError("Ingresa el nombre de la categoría");
      return;
    }

    try{
      setSaving(true);
      const editing=Boolean(selectedCategory);

      const response=await fetch(
        editing?`/competition/categories/${selectedCategory.slug}/`:"/competition/categories/",
        {
          method:editing?"PATCH":"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken"),
          },
          body:JSON.stringify(editing?{name:cleanName,active}:{name:cleanName}),
        }
      );

      const data=await readResponse(response);
      if(!response.ok) throw new Error(data.error||"No se pudo guardar la categoría.");

      handleClose();
      await loadCategories();
      showSuccess(editing?"Categoría actualizada correctamente.":"Categoría creada correctamente.");
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",gap:1.5,mb:2.5}}>
        <Box>
          <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},fontWeight:typography.weights.extraBold,color:colors.text}}>
            Categorías
          </Typography>

          <Typography sx={{mt:0.4,fontSize:typography.sizes.md,color:colors.textSecondary}}>
            Administra las categorías disponibles para los equipos.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleCreate}
          sx={{
            px:2.2,
            backgroundColor:colors.secondary,
            color:colors.tableHeaderText,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          Nueva categoría
        </Button>
      </Box>

      <Box sx={{mb:1.5,display:"flex",justifyContent:"flex-end"}}>
        <TextField
          value={search}
          onChange={(event)=>{
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder="Buscar categoría"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          width:"100%",
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radiusLarge,
          overflow:"hidden",
          backgroundColor:colors.surface,
        }}
      >
        <TableContainer sx={{overflowX:"auto"}}>
          <Table sx={{minWidth:600}}>
            <TableHead
              sx={{
                "& .MuiTableCell-root":{
                  backgroundColor:colors.tableHeader,
                  color:colors.tableHeaderText,
                  borderBottom:`1px solid ${colors.tableHeader}`,
                  fontWeight:typography.weights.bold,
                  fontSize:typography.sizes.sm,
                },
              }}
            >
              <TableRow>
                <TableCell>Categoría</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading&&(
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{py:5}}>
                    <CircularProgress
                      size={24}
                      sx={{color:colors.secondary}}
                    />
                  </TableCell>
                </TableRow>
              )}

              {!loading&&!rows.length&&(
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{py:5}}>
                    <Typography sx={{color:colors.textSecondary,fontSize:typography.sizes.md}}>
                      No hay categorías registradas.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading&&rows.map((category)=>(
                <TableRow
                  key={category.slug}
                  sx={{
                    "&:last-child td":{
                      borderBottom:0,
                    },
                    "&:hover":{
                      backgroundColor:colors.backgroundSoft,
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{fontWeight:typography.weights.bold,color:colors.text,fontSize:typography.sizes.md}}>
                      {category.name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{fontWeight:typography.weights.semibold,color:colors.text,fontSize:typography.sizes.md}}>
                      {category.active?"Activa":"Inactiva"}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={()=>handleEdit(category)}
                      sx={{
                        borderColor:colors.borderDark,
                        color:colors.text,
                        "&:hover":{
                          borderColor:colors.secondary,
                          color:colors.secondary,
                          backgroundColor:colors.backgroundSoft,
                        },
                      }}
                    >
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCategories.length}
          page={page}
          onPageChange={(event,newPage)=>setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event)=>{
            setRowsPerPage(parseInt(event.target.value,10));
            setPage(0);
          }}
          rowsPerPageOptions={[5,10,25]}
          labelRowsPerPage="Filas:"
          labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
          sx={{
            color:colors.textSecondary,
            backgroundColor:colors.surface,
            borderTop:`1px solid ${colors.border}`,
            ".MuiTablePagination-toolbar":{
              minHeight:52,
              px:{xs:1,sm:2},
              flexWrap:{xs:"wrap",sm:"nowrap"},
              justifyContent:{xs:"center",sm:"flex-end"},
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":{
              fontSize:typography.sizes.sm,
            },
          }}
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={saving?undefined:handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
            m:{xs:1.5,sm:3},
            width:{xs:"calc(100% - 24px)",sm:"100%"},
          },
        }}
      >
        <DialogTitle sx={{px:{xs:2,sm:2.5},pt:2.3,pb:1}}>
          <Typography sx={{fontSize:typography.sizes.xl,fontWeight:typography.weights.extraBold,color:colors.text}}>
            {selectedCategory?"Editar categoría":"Nueva categoría"}
          </Typography>

          <Typography sx={{mt:0.4,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
            {selectedCategory?"Actualiza la información de la categoría.":"Registra una nueva categoría para la competencia."}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{px:{xs:2,sm:2.5},pt:"14px !important"}}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(event)=>{
              setName(event.target.value);
              setNameError("");
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            fullWidth
            autoFocus
          />

          {selectedCategory&&(
            <FormControlLabel
              sx={{mt:1.5,ml:0,color:colors.text}}
              control={
                <Switch
                  checked={active}
                  onChange={(event)=>setActive(event.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked":{
                      color:colors.controlActive,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{
                      backgroundColor:colors.controlActive,
                    },
                  }}
                />
              }
              label={active?"Categoría activa":"Categoría inactiva"}
            />
          )}
        </DialogContent>

        <DialogActions sx={{px:{xs:2,sm:2.5},py:2,borderTop:`1px solid ${colors.border}`,gap:1}}>
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              color:colors.textSecondary,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor:colors.secondary,
              color:colors.tableHeaderText,
              "&:hover":{
                backgroundColor:colors.secondaryHover,
              },
            }}
          >
            {saving?"Guardando...":selectedCategory?"Guardar cambios":"Crear categoría"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}